import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch {
    return '';
  }
}

export const API = {
  getProjects: async () => {
    const res = await fetch(`${API_BASE}/projects`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch projects');
    return res.json();
  },

  createProject: async (data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create project');
    return res.json();
  },

  updateProject: async (projectId: string, data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...data, projectId }),
    });
    if (!res.ok) throw new Error('Failed to update project');
    return res.json();
  },

  deleteProject: async (projectId: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/projects/${projectId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete project');
    return res.json();
  },

  getTimeEntries: async (date?: string) => {
    const token = await getAuthToken();
    const url = date ? `${API_BASE}/time-entries?date=${date}` : `${API_BASE}/time-entries`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch time entries');
    return res.json();
  },

  createTimeEntry: async (data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/time-entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create time entry');
    return res.json();
  },

  updateTimeEntry: async (entryId: string, data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/time-entries/${entryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update time entry');
    return res.json();
  },

  deleteTimeEntry: async (entryId: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/time-entries/${entryId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete time entry');
    return res.json();
  },

  getTimesheets: async () => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/timesheets`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch timesheets');
    return res.json();
  },

  submitTimesheet: async (timesheetId: string, data: any = {}) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/timesheets/${timesheetId}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to submit timesheet');
    return res.json();
  },

  approveTimesheet: async (timesheetId: string, notes?: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/timesheets/${timesheetId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ notes }),
    });
    if (!res.ok) throw new Error('Failed to approve timesheet');
    return res.json();
  },

  rejectTimesheet: async (timesheetId: string, notes: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/timesheets/${timesheetId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ notes }),
    });
    if (!res.ok) throw new Error('Failed to reject timesheet');
    return res.json();
  },

  getExpenses: async (date?: string) => {
    const token = await getAuthToken();
    const url = date ? `${API_BASE}/expenses?date=${date}` : `${API_BASE}/expenses`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch expenses');
    return res.json();
  },

  createExpense: async (data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/expenses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create expense');
    return res.json();
  },

  deleteExpense: async (expenseId: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/expenses/${expenseId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete expense');
    return res.json();
  },

  getReceiptPresignedUrl: async (fileName: string, contentType: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/expenses/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fileName, contentType }),
    });
    if (!res.ok) throw new Error('Failed to get presigned URL');
    return res.json();
  },

  getHoursByProject: async (startDate?: string) => {
    const token = await getAuthToken();
    const url = startDate ? `${API_BASE}/reports/hours-by-project?startDate=${startDate}` : `${API_BASE}/reports/hours-by-project`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch report');
    return res.json();
  },

  getUtilization: async () => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/reports/utilization`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch utilization');
    return res.json();
  },

  getUsers: async () => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },

  createUser: async (data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create user');
    return res.json();
  },

  updateUser: async (userId: string, data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update user');
    return res.json();
  },

  updateExpense: async (expenseId: string, data: any) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/expenses/${expenseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update expense');
    return res.json();
  },
};
